import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Button } from "../../components/common/Button";
import {
  FormContainer,
  FormHeading,
  FormText,
} from "../../components/common/Form";
import {
  e2ePaymentEndpoint,
  invokeE2EPayment,
} from "../../shared/e2e-payment";
import { supabaseClient } from "../../shared/supabase";
import { useUserStore } from "../../stores/user.store";
import * as SentryUtils from "../../utils/sentry";
import { useCourseSubscriptionContext } from "./useCourseSubscriptionContext";

const SuccessIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      fill="currentColor"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
      fill="currentColor"
    />
  </svg>
);

const ContentContainer = styled.div`
  text-align: center;
  margin: 48px 0;
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const ButtonWrapper = styled.div`
  margin-top: 32px;
`;

type CheckoutStatus = "loading" | "success" | "error";

export const CourseConfirm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<CheckoutStatus>("loading");
  const { loadProfile } = useUserStore();
  const { setTempSubscriptions } = useCourseSubscriptionContext();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        let error;
        let data;
        if (e2ePaymentEndpoint) {
          try {
            data = await invokeE2EPayment(`/payment-status/${sessionId}`);
          } catch (paymentError) {
            error = paymentError;
          }
        } else {
          ({ error, data } = await supabaseClient.functions.invoke(
            `server/payment_status/${sessionId}`,
            { method: "GET" },
          ));
        }

        if (error) {
          SentryUtils.captureException(error, {
            tags: {
              component: "CourseConfirm",
              operation: "payment_status_verification",
            },
            extra: { sessionId },
          });
          console.error("Error checking session status:", error);
          setStatus("error");
          return;
        }

        const { status: paymentStatus } = data;

        if (paymentStatus === "complete") {
          setStatus("success");

          // Clean up temporary subscriptions since payment is complete
          setTempSubscriptions(new Map());

          // Refresh user profile to load new subscription data
          await loadProfile();

          // Redirect to courses page after 3 seconds on success
          setTimeout(async () => {
            await loadProfile();
            navigate("/account/courses");
          }, 3000);
        } else {
          setStatus("error");
        }
      } catch (error) {
        console.error("Error checking session status:", error);
        setStatus("error");
      }
    };

    checkStatus();
  }, [sessionId, navigate, loadProfile, setTempSubscriptions]);

  const handleRetryPayment = () => {
    navigate("/account/courses/checkout");
  };

  if (status === "loading") {
    return (
      <FormContainer>
        <ContentContainer>
          <FormHeading>VERIFICA PAGAMENTO IN CORSO</FormHeading>
          <FormText>ATTENDERE PREGO...</FormText>
        </ContentContainer>
      </FormContainer>
    );
  }

  if (status === "success") {
    return (
      <FormContainer>
        <ContentContainer>
          <IconWrapper>
            <SuccessIcon />
          </IconWrapper>
          <FormHeading>PAGAMENTO COMPLETATO</FormHeading>
          <FormText>
            IL TUO PAGAMENTO È STATO PROCESSATO CON SUCCESSO. LE ISCRIZIONI AI
            CORSI SONO STATE REGISTRATE. VERRAI REINDIRIZZATO ALLA PAGINA DEI
            CORSI.
          </FormText>
        </ContentContainer>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <ContentContainer>
        <IconWrapper>
          <ErrorIcon />
        </IconWrapper>
        <FormHeading>ERRORE NEL PAGAMENTO</FormHeading>
        <FormText>
          SI È VERIFICATO UN ERRORE DURANTE IL PROCESSO DI PAGAMENTO. RIPROVA O
          CONTATTA L'ASSISTENZA SE IL PROBLEMA PERSISTE.
        </FormText>
        <ButtonWrapper>
          <Button onClick={handleRetryPayment} fullWidth>
            RIPROVA IL PAGAMENTO
          </Button>
        </ButtonWrapper>
      </ContentContainer>
    </FormContainer>
  );
};
